import React, {FC, useEffect, useRef, useState} from 'react';

import {logger} from '../../../../../srcCommon/services/logger/logger';
import {reloadBrowser} from '../../../../services/browser';
import {getBuildVersion} from '../../../../services/buildVersion';
import {registerShowUpdateBannerFunction} from '../../../../services/electron/electronBridgeActions';
import {quitAndInstall} from '../../../../services/electron/electronBridgeOutbound';
import {HttpResponse, spyOnHttpResponses} from '../../../../services/http/httpBase';
import {NewVersionBanner, NewVersionBannersEnum} from './newVersionBanner';

/*
 * Hook.
 */

function useNewVersionBanner() {
  const wasClosedByUser = useRef(false);
  const [bannerType, setBannerType] = useState<NewVersionBannersEnum | null>(null);

  // Start spying on ANY http response on mount and display the banner once if the version returned by the backend
  // changed since the app was loaded. Cleanup the spying on unmount for the sake of completion, even though it should not happen.
  useEffect(() => {
    const stopSpying = spyOnHttpResponses((response) => {
      if (
        !wasClosedByUser.current &&
        bannerType !== NewVersionBannersEnum.NEW_ELECTRON_VERSION &&
        hasVersionChanged(response)
      ) {
        setBannerType(NewVersionBannersEnum.NEW_FRONTEND_VERSION);
      }
    });

    registerShowUpdateBannerFunction(() => {
      if (!wasClosedByUser.current) {
        setBannerType(NewVersionBannersEnum.NEW_ELECTRON_VERSION);
      }
    });

    return stopSpying;
  }, [bannerType]);

  function onRequestClose() {
    setBannerType(null);
    wasClosedByUser.current = true;
  }

  return {bannerType, onRequestClose};
}

function hasVersionChanged(response: HttpResponse) {
  const buildVersion = getBuildVersion();
  const responseVersion = response.headers['x-front-version'];
  return buildVersion !== 'unknown' && responseVersion && buildVersion !== responseVersion;
}

/*
 * Component.
 */

function bannerStateToReloadFunction(bannerType: NewVersionBannersEnum) {
  switch (bannerType) {
    case NewVersionBannersEnum.NEW_FRONTEND_VERSION:
      return reloadBrowser;
    case NewVersionBannersEnum.NEW_ELECTRON_VERSION:
    default:
      return () => {
        logger.info('Attempting to upgrade Electron app');
        quitAndInstall();
      };
  }
}

export const NewVersionBannerContainer: FC = () => {
  const {bannerType, onRequestClose} = useNewVersionBanner();
  if (!bannerType) {
    return null;
  }

  return (
    <NewVersionBanner
      onDismissClick={onRequestClose}
      onReloadClick={bannerStateToReloadFunction(bannerType)}
      bannerType={bannerType}
    />
  );
};
